import { computed, defineComponent } from "vue";
import { mapState, mapActions } from "pinia";

import { Container } from "aurelia-dependency-injection";

import config from "../../../common/config";
import { ILocation } from "../../vue-interfaces/i-location";
import { IWeatherRequestParams } from "../../vue-interfaces/i-weather";
import { Weather } from "../../../models/weather.model";
import {
    GeolocationService,
    GeoLocationEvents,
    requestDeviceLocation
} from "../../../services-vue/geolocation.service";
import { WeatherService } from "../../../services/weather.service";
import { AnalyticsService, AnalyticsCategory } from "../../../services/analytics.service";
import { weatherClass, formatingDateOrTime } from "../../../common/vue-helpers/modifiers/value-modifier";
import eventBus from "../../../../src/utilities/eventBus";
import { GetZipEvents } from "../../vue-dialogs/GetZip/GetZip";

import getZip from "../../vue-dialogs/GetZip/GetZip.vue";
import LocationSettings from "../../vue-dialogs/LocationSettings/LocationSettings.vue";

export default defineComponent({
    data() {
        return {
            duration: 5, // Weather report for N number of days
            durationDateFormat: "ddd", // Duration date format. Moment's date format
            _gl: null,
            _weatherService: null,
            _analyticsService: null,
            locationData: null,
            locationChanged: false,
            currentData: null,
            forecastData: [] as Array<Weather>,
            loading: false,
            locationCacheKey: config.locationCacheKey,
            weatherClass,
            formatingDateOrTime,
            dialogRef: null,
            showWeatherForecast: false,
            temperature: null,
            isCelsius: false
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    created() {
        this._gl = Container.instance.get(GeolocationService);
        this._weatherService = Container.instance.get(WeatherService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.initSubscriptions();
        this.currentData = Weather;
        this.locationData = {};
        this.isCelsius = false;
    },

    async mounted() {
        this.locationChanged = true;
        // This condition is required.
        // On route navigation, we will check whether location has been received already.
        if (this._gl.hasGeo) {
            Object.assign(this.locationData, this._gl.locationData);
        } else {
            await this.getLocationFromUser();
        }

        await this.init();
        document.addEventListener("click", this.handleClickOutside, true);
    },

    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside, true);
    },

    computed: {
        getClassOfCurrentWeather(): string {
            if (this.currentData.id) {
                return this.weatherClass(this.currentData?.id);
            } else {
                return "wi";
            }
        }
    },
    methods: {
        initSubscriptions() {
            eventBus.on(GeoLocationEvents.Retrieved, async (data: ILocation) => {
                Object.assign(this.locationData, data);
                await this.init();
            });

            eventBus.on(GetZipEvents.Changed, (zip: string) => {
                this.locationChanged = true;
            });
        },

        async getLocationFromUser(): Promise<void> {
            // https://geoip-db.com/json/ - Lat and Long based on IP address.
            // Alternate way to get location. Not accurate. Might get random countries.
            if (this._gl.hasGeo) {
                console.log(`GEOLOCATION: hasGeo is true. Why did you make a call? Culprit:`);
                return;
            }
            console.log("GEOLOCATION: Getting location");
            // FYI: getCurrentPosition() and watchPosition() no longer work on insecure origins.
            // To use this feature, you should consider switching your application to a secure origin, such as HTTPS.
            // See https://goo.gl/rStTGz for more details.
            requestDeviceLocation(
                this._gl,
                async (reason) => {
                    if (reason === "unsupported") {
                        this.logEvent(AnalyticsCategory.AxxessPlannerEditLocation, "No-Geolocation");
                    } else {
                        this.logEvent(AnalyticsCategory.GeoLocation, "User-Blocked");
                    }
                    await this.handleNoGeoLocation();
                },
                (source) => {
                    this.logEvent(
                        AnalyticsCategory.AxxessPlannerEditLocation,
                        source === "cache" ? "Old-Location" : "New-Location"
                    );
                }
            );
        },

        async handleNoGeoLocation(): Promise<void> {
            const userLocation = localStorage.getItem("axxess-user-location");
            if (userLocation) {
                const locationJSON = JSON.parse(userLocation);
                const willingToShare = locationJSON.willingToShareLocation;
                const fromGeo = locationJSON.fromGeo;

                if (willingToShare) {
                    // Check to see if User blocked after allowing geolocation
                    if (!fromGeo) {
                        await this._gl.getCoordinatesByZip(locationJSON.zip);
                    } else {
                        await this.getLocationUsingZip();
                    }
                } else {
                    eventBus.emit(GetZipEvents.Canceled);
                }
            } else {
                await this.getLocationUsingZip();
            }
        },

        async getLocationUsingZip() {
            let zipOptions = {
                zip: this.locationData.zip
            };

            this.dialogRef = this.$dialog.open(getZip, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    zipOptions
                },
                onClose: (response: any) => {
                    if (response.data !== "success") {
                        this.cacheUnwillingToShareLocation();
                    }
                }
            });
            eventBus.on(GetZipEvents.Canceled, () => {
                this.cacheUnwillingToShareLocation();
            });
        },

        cacheUnwillingToShareLocation() {
            Object.assign(this.locationData, {
                willingToShareLocation: false,
                fromGeo: false
            });
            this._gl.cacheLocation();
        },

        async init(): Promise<void> {
            const options = this.getWeatherRequestData();

            if (!!options) {
                this.loading = true;
                const forecastOptions = Object.assign(options, {
                    count: 6
                });
                const currentWeatherReq = await this._weatherService.getCurrentWeather(options, this.locationChanged);
                const forecastWeatherReq = await this._weatherService.getWeatherForecast(
                    forecastOptions,
                    this.locationChanged
                );
                this.currentData = currentWeatherReq;
                this.temperature = this.currentData.temp;
                this.forecastData = forecastWeatherReq;
                this.locationChanged = false;
                const todayForecast = this.forecastData.splice(0, 1)[0];
                this.currentData.updateHiLo(
                    {
                        hi: todayForecast.hi,
                        lo: todayForecast.lo
                    },
                    false
                );
                this.loading = false;
            }
        },

        getWeatherRequestData() {
            const locationKey: string = localStorage.getItem(this.locationCacheKey);
            if (locationKey) {
                this.locationData = JSON.parse(locationKey);
            }

            if (this.locationData && !$.isEmptyObject(this.locationData)) {
                const reqParams: IWeatherRequestParams = {
                    units: "imperial" // Default: Kelvin, Metric: Celsius, Imperial: Fahrenheit
                };

                if (this.locationData.lat && this.locationData.lon) {
                    return Object.assign(reqParams, {
                        latitude: this.locationData.lat,
                        longitude: this.locationData.lon
                    });
                } else if (this.locationData.zip) {
                    return Object.assign(reqParams, {
                        zip: `${this.locationData.zip},us`
                    });
                } else {
                    console.warn("get weatherRequestData(): Latitude, longitude or zip was not set in locationData.");
                }
            } else {
                console.warn("get weatherRequestData(): locationData is empty or undefined.");
            }
            return null;
        },

        // This Method pops the dialog for the user to enter zipCode.
        // Once the zip code changes, need to get the coordinates of the entered zip code.
        // Once we get the coordinates of the zip code, we need to initialize the report again.
        async updateLocation() {
            let zipOptions = {
                zip: this.locationData.zip
            };

            this.dialogRef = this.$dialog.open(getZip, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    zipOptions
                },
                onClose: (response: any) => {
                    if (response.data !== "success") {
                        this.cacheUnwillingToShareLocation();
                    }
                }
            });
        },

        logEvent(category: string, action: string) {
            this._analyticsService.logEvent({ category, action });
        },

        toggleWeatherForecast(): void {
            this.showWeatherForecast = !this.showWeatherForecast;
        },
        handleClickOutside(event: MouseEvent): void {
            if (!this.showWeatherForecast) {
                return;
            }
            const forecastEl = this.$refs.weatherForecastRef as HTMLElement | undefined;
            const toggleIconEl = this.$refs.toggleIconRef as HTMLElement | undefined;
            const target = event.target as Node;
            const clickedInsideForecast = forecastEl?.contains(target);
            const clickedToggleIcon = toggleIconEl?.contains(target);

            if (!clickedInsideForecast && !clickedToggleIcon) {
                this.showWeatherForecast = false;
            }
        },

        openSettings() {
            const locationData = {
                locations: this.locationData,
                celsius: this.isCelsius
            };
            this.showWeatherForecast = false;
            this.dialogRef = this.$dialog.open(LocationSettings, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 375px"
                },
                data: {
                    locationData
                },
                onClose: (options) => {
                    if (options.data.isCelOrFah === true) {
                        this.isCelsius = true;
                        let temperature = ((this.currentData.temp - 32) * 5) / 9;
                        this.temperature = temperature.toFixed(0);
                    } else {
                        this.isCelsius = false;
                        this.temperature = this.currentData.temp;
                    }
                }
            });
        }
    }
});
